import { sleep } from "@/lib/utils";

import type { Api, InspirationFeedItem } from "../types";
import { products } from "./products";

type FeedRecord = Omit<InspirationFeedItem, "product">;

let records: FeedRecord[] = [
	{
		id: "ins_demo_1",
		productId: "prd_demo_3",
		sortOrder: 0,
		editorialTitle: "Soft tailoring for every day",
		editorialNote: "Lead card for the client inspiration feed.",
		createdAt: "2026-05-01T10:00:00Z",
		updatedAt: "2026-05-01T10:00:00Z",
	},
	{
		id: "ins_demo_2",
		productId: "prd_demo_4",
		sortOrder: 1,
		editorialTitle: "Warm corners",
		editorialNote: "Good home object for discovery traffic.",
		createdAt: "2026-05-01T10:05:00Z",
		updatedAt: "2026-05-01T10:05:00Z",
	},
];

const hydrate = async (record: FeedRecord): Promise<InspirationFeedItem> => ({
	...record,
	product: await products.get(record.productId),
});

const orderedRecords = () => [...records].sort((a, b) => a.sortOrder - b.sortOrder);

export const inspirationFeed: Api["inspirationFeed"] = {
	async list() {
		await sleep(150);
		const items: InspirationFeedItem[] = [];
		for (const record of orderedRecords()) items.push(await hydrate(record));
		return items;
	},

	async add(input) {
		await sleep(180);
		if (records.some((record) => record.productId === input.productId)) {
			throw new Error("Product is already in the inspiration feed");
		}

		const product = await products.get(input.productId);
		if (product.status !== "published") {
			throw new Error("Only published products can be added to the inspiration feed");
		}

		const now = new Date().toISOString();
		const record: FeedRecord = {
			id: `ins_${crypto.randomUUID()}`,
			productId: input.productId,
			sortOrder: records.length,
			editorialTitle: input.editorialTitle,
			editorialNote: input.editorialNote,
			createdAt: now,
			updatedAt: now,
		};
		records = [...records, record];
		return { ...record, product };
	},

	async update(id, patch) {
		await sleep(150);
		const now = new Date().toISOString();
		records = records.map((record) =>
			record.id === id
				? {
						...record,
						editorialTitle: patch.editorialTitle,
						editorialNote: patch.editorialNote,
						updatedAt: now,
					}
				: record,
		);
		const found = records.find((record) => record.id === id);
		if (!found) throw new Error("Inspiration feed item not found");
		return hydrate(found);
	},

	async reorder(ids) {
		await sleep(180);
		const next: FeedRecord[] = [];
		for (const id of ids) {
			const found = records.find((record) => record.id === id);
			if (found) next.push(found);
		}
		for (const record of records) {
			if (!ids.includes(record.id)) next.push(record);
		}
		const now = new Date().toISOString();
		records = next.map((record, index) => ({
			...record,
			sortOrder: index,
			updatedAt: now,
		}));
		return this.list();
	},

	async remove(id) {
		await sleep(150);
		records = records
			.filter((record) => record.id !== id)
			.map((record, index) => ({ ...record, sortOrder: index }));
	},
};
